import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Paper } from "@material-ui/core";
import { primary, darkBlue } from "../../styles/colors";
import { formatCurrency, formatDate, getPaymentStatus, duePaymentsService } from "./duePaymentsService";
import { GridCloseIcon } from "@mui/x-data-grid";
import { IconButton } from "@mui/material";

const PaymentDetailPopup = ({ open, onClose, payment }) => {
  if (!payment) return null;

  const status = getPaymentStatus(payment?.dueDate);
  const discountAmount = payment?.discount?.eligible
    ? duePaymentsService.calculateDiscount(payment?.amountDue || 0, payment?.discount?.percentage || 0)
    : 0;
  const finalAmount = (payment?.amountDue || 0) - discountAmount;

  const getStatusConfig = () => {
    const configs = {
      overdue: { bg: "#f8d7da", color: "#721c24", label: "Overdue" },
      urgent: { bg: "#fff3cd", color: "#856404", label: "Urgent" },
      pending: { bg: "#cce5ff", color: "#004085", label: "Pending" },
      paid: { bg: "#d4edda", color: "#155724", label: "Paid" },
    };
    return configs[status] || configs.pending;
  };

  const statusConfig = getStatusConfig();

  const InfoRow = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #e9ecef" }}>
      <span style={{ color: "#6c757d", fontSize: "0.85rem" }}>{label}</span>
      <span style={{ fontWeight: 500, color: darkBlue }}>{value || "N/A"}</span>
    </div>
  );

  const isMiller = payment?.type === "miller";
  const displayType = payment?.type?.charAt(0)?.toUpperCase() + (payment?.type?.slice(1) || "");
  const entityName = payment?.entityName || payment?.name || "N/A";
  const discountPercentage = payment?.discount?.percentage || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "12px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: primary,
          color: "white",
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>Payment Details</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
          <GridCloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: "24px" }}>
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <span style={{ fontSize: "1.2rem", fontWeight: 600, color: darkBlue }}>{entityName}</span>
            <span
              style={{
                padding: "6px 12px",
                borderRadius: "16px",
                fontSize: "0.75rem",
                fontWeight: 600,
                backgroundColor: statusConfig.bg,
                color: statusConfig.color,
              }}
            >
              {statusConfig.label}
            </span>
          </div>
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "12px",
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: isMiller ? "rgba(241, 154, 48, 0.15)" : "rgba(30, 58, 138, 0.15)",
              color: isMiller ? primary : "#1E3A8A",
            }}
          >
            {displayType || "N/A"}
          </span>
        </div>

        <Paper elevation={0} style={{ padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: "20px" }}>
          <InfoRow label="Bill ID" value={payment?.billId || payment?.invoice_id || "N/A"} />
          <InfoRow label="Purchase Order" value={payment?.poId || payment?.purchase_order_id || "N/A"} />
          {/* <InfoRow label="Sales Order" value={payment?.soId || payment?.sales_order_id || "N/A"} /> */}
          <InfoRow label="Due Date" value={formatDate(payment?.dueDate)} />
          <InfoRow label="Amount Due" value={formatCurrency(payment?.amountDue || 0, payment?.currency)} />
          <InfoRow label="Amount Paid" value={formatCurrency(payment?.amountPaid || 0, payment?.currency)} />
        </Paper>

        {payment?.discount?.eligible && (
          <Paper
            elevation={0}
            style={{
              background: "rgba(255, 215, 0, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 215, 0, 0.3)",
              marginBottom: "20px",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "16px", color: darkBlue, display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "1.2rem" }}>💰</span>
              Discount Savings Breakdown
            </div>

            <div style={{ position: "relative", height: "24px", marginBottom: "16px" }}>
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  width: `${100 - discountPercentage}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #FF3D00, #FF6B3D)",
                  borderRadius: "12px 0 0 12px",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  width: `${discountPercentage}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #FFD700, #FFA500)",
                  borderRadius: "0 12px 12px 0",
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ color: "#FF3D00" }}>Original: {formatCurrency(payment?.amountDue || 0, payment?.currency)}</span>
              <span style={{ color: "#FFD700", fontWeight: 600 }}>
                Save: {formatCurrency(discountAmount, payment?.currency)}
              </span>
            </div>

            {payment?.discount?.expiryDays && (
              <div style={{ fontSize: "0.75rem", color: "#856404", marginBottom: "12px" }}>
                Pay within {payment.discount.expiryDays} days to avail this discount
              </div>
            )}

            <div
              style={{
                marginTop: "12px",
                paddingTop: "12px",
                borderTop: "1px dashed #E8E8E8",
                fontWeight: 600,
                fontSize: "1.1rem",
                color: darkBlue,
              }}
            >
              Final Amount: {formatCurrency(finalAmount, payment?.currency)}
            </div>
          </Paper>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid #e9ecef" }}>
        <Button onClick={onClose} style={{ color: "#6c757d" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDetailPopup;
